/**
 * errorLogger — client-side error capture that POSTs to /api/log (D1 backed).
 *
 * Call installErrorLogger() once at app startup. It installs:
 *   - window.onerror
 *   - window.onunhandledrejection
 *   - a Google Maps listener for the "gm_authFailure" callback (auth/billing/referrer errors)
 *   - logError(source, message, extra?) for manual reporting from anywhere
 *
 * Errors are batched (flush on interval or when buffer fills) so network failure
 * doesn't lose context. A duplicate suppressor prevents the same repeated error
 * from spamming the backend. Fails silent — logging must never crash the app.
 */

import { telegram } from './telegram';
import { attemptRuntimeRecovery, isRecoverableRuntimeFailure } from './runtimeRecovery';

interface LogEntry {
  level?: 'error' | 'warn' | 'info' | 'maps';
  source: string;
  message: string;
  stack?: string;
  url?: string;
  extra?: unknown;
}

const BUFFER: LogEntry[] = [];
const MAX_BUFFER = 20;
const FLUSH_INTERVAL_MS = 5000;
const DEDUP_TTL_MS = 10_000;
const recentSignatures = new Map<string, number>();

let flushTimer: number | null = null;

const signatureOf = (e: LogEntry): string =>
  `${e.source}|${e.message?.slice(0, 200) || ''}`;

const flushNow = async () => {
  if (BUFFER.length === 0) return;
  const batch = BUFFER.splice(0, BUFFER.length);
  const telegramUserId = (() => {
    try { return telegram.getUserId() ?? null; } catch { return null; }
  })();
  await Promise.all(batch.map(entry =>
    fetch('/api/log', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      // keepalive lets the request complete even if the page is unloading
      keepalive: true,
      body: JSON.stringify({
        level: entry.level || 'error',
        source: entry.source,
        message: entry.message,
        stack: entry.stack,
        url: entry.url || (typeof location !== 'undefined' ? location.href : ''),
        telegramUserId,
        extra: entry.extra,
      }),
    }).catch(() => { /* swallow */ })
  ));
};

const scheduleFlush = () => {
  if (flushTimer != null) return;
  flushTimer = window.setTimeout(() => {
    flushTimer = null;
    void flushNow();
  }, FLUSH_INTERVAL_MS);
};

export function logError(
  source: string,
  message: string,
  extra?: { stack?: string; level?: LogEntry['level']; url?: string; extra?: unknown }
): void {
  try {
    const entry: LogEntry = {
      source,
      message,
      level: extra?.level || 'error',
      stack: extra?.stack,
      url: extra?.url,
      extra: extra?.extra,
    };
    const sig = signatureOf(entry);
    const now = Date.now();
    for (const [key, ts] of recentSignatures) {
      if (now - ts > DEDUP_TTL_MS) recentSignatures.delete(key);
    }
    if (recentSignatures.has(sig)) return;
    recentSignatures.set(sig, now);

    BUFFER.push(entry);
    if (BUFFER.length >= MAX_BUFFER) {
      void flushNow();
    } else {
      scheduleFlush();
    }
  } catch {
    /* never throw from logger */
  }
}

let installed = false;
export function installErrorLogger(): void {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (event) => {
    const message = event.message || 'unknown error';
    if (isRecoverableRuntimeFailure(message, event.error?.stack)) {
      void attemptRuntimeRecovery('window-error');
    }
    logError('window.onerror', event.message || 'unknown error', {
      stack: event.error?.stack,
      extra: {
        filename: event.filename,
        line: event.lineno,
        col: event.colno,
      },
    });
  });

  window.addEventListener('unhandledrejection', (event) => {
    const reason: any = event.reason;
    const message = typeof reason === 'string'
      ? reason
      : reason?.message || 'unhandled rejection';
    if (isRecoverableRuntimeFailure(message, reason?.stack)) {
      void attemptRuntimeRecovery('unhandled-rejection');
    }
    logError('unhandledrejection', message, {
      stack: reason?.stack,
      extra: typeof reason === 'object' ? { name: reason?.name } : undefined,
    });
  });

  // Google Maps specific: this callback fires when the key is rejected
  // (billing / referrer / quota / activation errors). All other Maps API
  // problems surface here.
  (window as any).gm_authFailure = () => {
    logError('googleMaps', 'Google Maps auth failure (billing / referrer / quota / key invalid)', {
      level: 'maps',
      extra: {
        href: location.href,
        referrer: document.referrer,
      },
    });
  };

  // Flush any buffered entries before the page hides (Telegram Mini App closes, etc.)
  window.addEventListener('pagehide', () => { void flushNow(); });
  window.addEventListener('beforeunload', () => { void flushNow(); });
}

export const __flushErrorLogger = flushNow; // exported for tests
